import { useState } from "react";
import { ChevronRight, CircleCheck, Circle, CalendarDays, Flag } from "lucide-react";
import { TASK_STATUSES, PRIORITY_MAP, isOverdue, fmtDate } from "@/lib/taskConstants";
import { UserAvatar } from "@/components/Avatars";

const RowContent = ({ task, members, onSelect, onToggleDone, isSub }) => {
  const assignee = members.find((m) => m.profile_id === task.assignee_id);
  const overdue = isOverdue(task);
  return (
    <div
      className={`flex items-center gap-2 px-3 py-2.5 hover:bg-secondary/60 cursor-pointer group border-b border-border/50 ${isSub ? "pl-12" : ""}`}
      onClick={() => onSelect(task.id)}
      data-testid={`list-task-row-${task.id}`}
    >
      <button onClick={(e) => { e.stopPropagation(); onToggleDone(task); }}
        data-testid={`list-toggle-done-${task.id}`} className="shrink-0">
        {task.status === "done"
          ? <CircleCheck className="h-[18px] w-[18px] text-[#1E8E3E]" />
          : <Circle className="h-[18px] w-[18px] text-muted-foreground group-hover:text-foreground" />}
      </button>
      <span className={`text-sm flex-1 min-w-0 truncate ${task.status === "done" ? "line-through text-muted-foreground" : ""}`}>
        {task.title}
      </span>
      <Flag className="h-3.5 w-3.5 shrink-0" style={{ color: PRIORITY_MAP[task.priority]?.color }} />
      {(task.start_date || task.due_date) && (
        <span className={`text-xs items-center gap-1 shrink-0 hidden sm:flex ${overdue ? "text-destructive font-medium" : "text-muted-foreground"}`}>
          <CalendarDays className="h-3 w-3" />
          {task.start_date && task.due_date
            ? `${fmtDate(task.start_date)} → ${fmtDate(task.due_date)}`
            : fmtDate(task.due_date || task.start_date)}
        </span>
      )}
      {assignee ? (
        <UserAvatar name={assignee.name} color={assignee.avatar_color} size="sm" className="!h-6 !w-6 !text-[10px] shrink-0" />
      ) : (
        <span className="h-6 w-6 rounded-full border border-dashed border-muted-foreground/40 shrink-0" />
      )}
    </div>
  );
};

const ParentRow = ({ task, subtasks, members, onSelect, onToggleDone }) => {
  const [expanded, setExpanded] = useState(true);
  const doneSubs = subtasks.filter((s) => s.status === "done").length;
  return (
    <div>
      <div className="flex items-center">
        <div className="pl-3 shrink-0">
          {subtasks.length > 0 ? (
            <button onClick={() => setExpanded(!expanded)}
              data-testid={`list-expand-btn-${task.id}`}
              className="text-muted-foreground hover:text-foreground flex items-center gap-1">
              <ChevronRight className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-90" : ""}`} />
              <span className="text-[10px]">{doneSubs}/{subtasks.length}</span>
            </button>
          ) : <span className="inline-block w-3.5" />}
        </div>
        <div className="flex-1 min-w-0">
          <RowContent task={task} members={members} onSelect={onSelect} onToggleDone={onToggleDone} isSub={false} />
        </div>
      </div>
      {expanded && subtasks.map((st) => (
        <RowContent key={st.id} task={st} members={members} onSelect={onSelect} onToggleDone={onToggleDone} isSub={true} />
      ))}
    </div>
  );
};

export const ListView = ({ tasks, subtasksByParent, members, onSelect, onToggleDone }) => {
  return (
    <div className="rounded-xl border bg-card overflow-hidden" data-testid="list-view">
      {TASK_STATUSES.map((status) => {
        const group = tasks.filter((t) => t.status === status.value);
        if (group.length === 0) return null;
        return (
          <div key={status.value}>
            <div className="px-3 py-2 bg-secondary/50 flex items-center gap-2 border-b border-border/50">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: status.color }} />
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {status.label} · {group.length}
              </span>
            </div>
            {group.map((t) => (
              <ParentRow key={t.id} task={t} subtasks={subtasksByParent[t.id] || []} members={members}
                onSelect={onSelect} onToggleDone={onToggleDone} />
            ))}
          </div>
        );
      })}
      {tasks.length === 0 && (
        <p className="p-10 text-center text-sm text-muted-foreground">No hay tareas que coincidan con los filtros.</p>
      )}
    </div>
  );
};
